import _cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from 'react';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Button from '@material-ui/core/Button';
import getFormStatus from 'Components/Forms/Functions/getFormStatus';
import Loader from 'Pure/Loader';
import bemit from 'Functions/bemit';
import ajaxRequest from 'Functions/ajax/ajaxRequest';
import { action } from 'Shared/Routes';
import { formatNum } from 'Shared/utilities';
const messages_config = {
  'succeeded': {
    info: 'Paiement réussi.'
  },
  'processing': {
    info: 'Paiement en cours.'
  },
  'default': {
    error: 'Une erreur non identifiée est survenue.'
  }
};
const input_classes = {
  base: bemit('c-stripe-field'),
  complete: bemit('c-stripe-field', ['complete']),
  empty: bemit('c-stripe-field', ['empty']),
  focus: bemit('c-stripe-field', ['focus']),
  invalid: bemit('c-stripe-field', ['invalid']),
  webkitAutofill: bemit('c-stripe-field', ['webkit-filled'])
};
const cc_number_options = {
  classes: input_classes,
  placeholder: 'Numéro de carte',
  style: {
    base: {
      color: '#555',
      fontFamily: 'Noto Sans, Roboto, Open Sans, Segoe UI, sans-serif',
      fontSize: '16px'
    }
  }
};
const cc_exp_options = _cloneDeep(cc_number_options);

// @ts-ignore
delete cc_exp_options.placeholder;
const cc_cvv_options = _cloneDeep(cc_number_options);
cc_cvv_options.placeholder = 'Code de sécurité CVV';

/**
 * @param { Object } props
 * @param { String } props.intent
 * @param { Object } props.customer
 * @param { function } props.onSuccess
 * @param { String } [props.booking_id]
 * @param { String } [props.booking_services_id]
 * @param { String } [props.packorder_id]
 * @param { String|Number } props.amount
 *
 * @returns { React.ReactElement }
**/
export default function StripeFields({
  intent,
  booking_id,
  ...props
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmation_error, setConfirmationError] = useState();
  useEffect(() => {
    if (!stripe || !intent) return;
    stripe.retrievePaymentIntent(intent).then(function ({
      paymentIntent
    }) {
      const status = paymentIntent?.status || 'requires_payment_method';
      if (['processing', 'succeeded'].includes(status)) {
        return props.onSuccess();
      }
      const info_msg = messages_config[status];
      if (info_msg) setMessage(info_msg);
    });
  }, [stripe]);
  if (confirmation_error) {
    return getFormStatus({
      error: confirmation_error
    }, ['v2']);
  }
  const loader = loading === true ? /*#__PURE__*/React.createElement(Loader, {
    className: "c-loader",
    bemit: ['white', 'bg-primary']
  }) : undefined;
  const btn_disabled = !stripe || !elements;
  return /*#__PURE__*/React.createElement("form", {
    className: "c-stripe-form__fields",
    onSubmit: e => e.preventDefault()
  }, /*#__PURE__*/React.createElement("div", {
    className: "c-stripe-form__field-container field-cc-num"
  }, /*#__PURE__*/React.createElement(CardNumberElement, {
    options: cc_number_options
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "c-stripe-form__field-popup direction-down icon-lock"
  }, /*#__PURE__*/React.createElement("span", {
    className: "c-stripe-form__field-popup-txt"
  }, "Toutes les transactions sont s\xE9curis\xE9es et chiffr\xE9es."))), /*#__PURE__*/React.createElement("div", {
    className: "c-stripe-form__field-container field-cc-exp"
  }, /*#__PURE__*/React.createElement(CardExpiryElement, {
    options: cc_exp_options
  })), /*#__PURE__*/React.createElement("div", {
    className: "c-stripe-form__field-container field-cc-code"
  }, /*#__PURE__*/React.createElement(CardCvcElement, {
    options: cc_cvv_options
  }), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "c-stripe-form__field-popup"
  }, /*#__PURE__*/React.createElement("span", {
    className: "c-stripe-form__field-popup-txt"
  }, "Code de s\xE9curit\xE9 \xE0 3 chiffres g\xE9n\xE9ralement situ\xE9 \xE0 l'endos de votre carte."))), getFormStatus(message, ['v2', 'no-margins']), /*#__PURE__*/React.createElement("div", {
    className: "c-stripe-form__actions"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    className: bemit('c-flat-btn', ['front', 'blue'].concat(btn_disabled ? ['disabled'] : [])),
    disabled: !stripe || !elements,
    onClick: handleSubmit
  }, "Valider et payer", ' ', formatNum(props.amount, '0,0.00'), "\xA0\u20AC", props.packorder_id ? ' TTC' : undefined, loader)));

  /**
   * @description Save card infos as a stripe payment method and then use this
   * method to fire the payment intent confirmation
   *
   * @returns { Promise<void|undefined> }
  **/
  async function handleSubmit() {
    if (!stripe || !elements || loading) return;
    setLoading(true);
    const billing_details = {
      name: [props.customer.firstname, props.customer.lastname].filter(str => str && typeof str === 'string').join(' '),
      email: props.customer.login
    };
    const response = await stripe.createPaymentMethod({
      type: 'card',
      card: elements.getElement(CardNumberElement),
      billing_details: billing_details
    });
    const {
      paymentMethod: payment_method,
      error
    } = response;
    if (error && (booking_id || props.booking_services_id)) {
      ajaxRequest(action('StripePaymentError'), {
        data: props.booking_services_id ? {
          booking_services_id: props.booking_services_id
        } : {
          booking_id: booking_id
        }
      });
    }
    if (error?.message) {
      setLoading(false);
      return setMessage({
        error: error.message
      });
    } else if (error || !payment_method?.id) {
      setLoading(false);
      return setMessage(messages_config.default);
    }
    await confirmPayment(payment_method.id);
  }

  /**
   * @description Use the newly created payment method to process the payment
   * by confirming the payment intent
   *
   * @param { String } payment_method_id
   *
   * @returns { Promise<void|undefined> }
  **/
  async function confirmPayment(payment_method_id) {
    if (!stripe || !elements) return;
    const response = await stripe.confirmCardPayment(intent, {
      payment_method: payment_method_id
    });
    const {
      error,
      paymentIntent: payment
    } = response;
    const status = payment?.status || 'requires_payment_method';
    if (error && (booking_id || props.booking_services_id)) {
      ajaxRequest(action('StripePaymentError'), {
        data: props.booking_services_id ? {
          booking_services_id: props.booking_services_id
        } : {
          booking_id: booking_id
        }
      });
    }
    if (error?.message) {
      setLoading(false);
      return setMessage({
        error: error.message
      });
    } else if (error || ['requires_payment_method', 'canceled'].includes(status)) {
      setLoading(false);
      return setMessage(messages_config.default);
    }
    sendConfirmation();
  }

  /**
   * @description Ask the server to validate the order and send corresponding emails
   *
   * @returns { Promise<void|undefined> }
  **/
  async function sendConfirmation() {
    const action_name = props.booking_services_id ? 'ValidateServicesStripePayment' : props.packorder_id ? 'ValidatePackOrderStripePayment' : 'ValidateStripePayment';
    try {
      await ajaxRequest(action(action_name), {
        data: props.booking_services_id ? {
          booking_services_id: props.booking_services_id
        } : props.packorder_id ? {
          packorder_id: props.packorder_id
        } : {
          booking_id: booking_id
        }
      });
    } catch (e) {
      console.error(e);
      const error_msg = e && typeof e.error === 'string' ? e.error : messages_config['default'];
      return setConfirmationError(error_msg);
    }
    props.onSuccess();
  }
}
